/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/consistent-type-definitions */
/**
 * Logic:
 *  When `mode` === `picker`,
 *  click will trigger `onSelect` (if value changed trigger `onChange` also).
 *  Panel change will not trigger `onSelect` but trigger `onPanelChange`
 */
import * as React from 'react';
import classNames from 'classnames';
import KeyCode from 'rc-util/lib/KeyCode';
import warning from 'rc-util/lib/warning';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import TimePanel from './panels/TimePanel';
import DatetimePanel from './panels/DatetimePanel';
import DatePanel from './panels/DatePanel';
import WeekPanel from './panels/WeekPanel';
import MonthPanel from './panels/MonthPanel';
import QuarterPanel from './panels/QuarterPanel';
import YearPanel from './panels/YearPanel';
import DecadePanel from './panels/DecadePanel';
import { isEqual } from './utils/dateUtil';
import PanelContext from './PanelContext';
import { PickerModeMap } from './utils/uiUtil';
import RangeContext from './RangeContext';
import getExtraFooter from './utils/getExtraFooter';
import getRanges from './utils/getRanges';
import { getLowerBoundTime, setDateTime, setTime } from './utils/timeUtil';
function PickerPanel(props) {
    const { prefixCls = 'rc-picker', className, style, locale, generateConfig, value, defaultValue, pickerValue, defaultPickerValue, disabledDate, mode, picker = 'date', tabIndex = 0, showNow, showTime, showToday, renderExtraFooter, hideHeader, onSelect, onChange, onPanelChange, onMouseDown, onPickerValueChange, onOk, components, direction, hourStep = 1, minuteStep = 1, secondStep = 1, } = props;
    const needConfirmButton = (picker === 'date' && !!showTime) || picker === 'time';
    const isHourStepValid = 24 % hourStep === 0;
    const isMinuteStepValid = 60 % minuteStep === 0;
    const isSecondStepValid = 60 % secondStep === 0;
    if (process.env.NODE_ENV !== 'production') {
        warning(!value || generateConfig.isValidate(value), 'Invalidate date pass to `value`.');
        warning(!value || generateConfig.isValidate(value), 'Invalidate date pass to `defaultValue`.');
        warning(isHourStepValid, `\`hourStep\` ${hourStep} is invalid. It should be a factor of 24.`);
        warning(isMinuteStepValid, `\`minuteStep\` ${minuteStep} is invalid. It should be a factor of 60.`);
        warning(isSecondStepValid, `\`secondStep\` ${secondStep} is invalid. It should be a factor of 60.`);
    }
    // ============================ State =============================
    const panelContext = React.useContext(PanelContext);
    const { operationRef, panelRef: panelDivRef, onSelect: onContextSelect, hideRanges, defaultOpenValue, } = panelContext;
    const { inRange, panelPosition, rangedValue, hoverRangedValue } = React.useContext(RangeContext);
    const panelRef = React.useRef({});
    // Handle init logic
    const initRef = React.useRef(true);
    // Value
    const [mergedValue, setInnerValue] = useMergedState(null, {
        value,
        defaultValue,
        postState: val => {
            if (!val && defaultOpenValue && picker === 'time') {
                return defaultOpenValue;
            }
            return val;
        },
    });
    // View date control
    const [viewDate, setInnerViewDate] = useMergedState(null, {
        value: pickerValue,
        defaultValue: defaultPickerValue || mergedValue,
        postState: date => {
            const now = generateConfig.getNow();
            if (!date) {
                return now;
            }
            // When value is null and set showTime
            if (!mergedValue && showTime) {
                if (typeof showTime === 'object') {
                    return setDateTime(generateConfig, date, showTime.defaultValue || now);
                }
                if (defaultValue) {
                    return setDateTime(generateConfig, date, defaultValue);
                }
                return setDateTime(generateConfig, date, now);
            }
            return date;
        },
    });
    const setViewDate = (date) => {
        setInnerViewDate(date);
        if (onPickerValueChange) {
            onPickerValueChange(date);
        }
    };
    // Panel control
    const getInternalNextMode = (nextMode) => {
        const getNextMode = PickerModeMap[picker];
        if (getNextMode) {
            return getNextMode(nextMode);
        }
        return nextMode;
    };
    // Save panel is changed from which panel
    const [mergedMode, setInnerMode] = useMergedState(() => {
        if (picker === 'time') {
            return 'time';
        }
        return getInternalNextMode('date');
    }, {
        value: mode,
    });
    React.useEffect(() => {
        setInnerMode(picker);
    }, [picker]);
    const [sourceMode, setSourceMode] = React.useState(() => mergedMode);
    const onInternalPanelChange = (newMode, viewValue) => {
        const nextMode = getInternalNextMode(newMode || mergedMode);
        setSourceMode(mergedMode);
        setInnerMode(nextMode);
        if (onPanelChange && (mergedMode !== nextMode || isEqual(generateConfig, viewDate, viewDate))) {
            onPanelChange(viewValue, nextMode);
        }
    };
    const triggerSelect = (date, type, forceTriggerSelect = false) => {
        if (mergedMode === picker || forceTriggerSelect) {
            setInnerValue(date);
            if (onSelect) {
                onSelect(date);
            }
            if (onContextSelect) {
                onContextSelect(date, type);
            }
            if (onChange && !isEqual(generateConfig, date, mergedValue) && !(disabledDate === null || disabledDate === void 0 ? void 0 : disabledDate(date))) {
                onChange(date);
            }
        }
    };
    // ========================= Interactive ==========================
    const onInternalKeyDown = (e) => {
        if (panelRef.current && panelRef.current.onKeyDown) {
            if ([
                KeyCode.LEFT,
                KeyCode.RIGHT,
                KeyCode.UP,
                KeyCode.DOWN,
                KeyCode.PAGE_UP,
                KeyCode.PAGE_DOWN,
                KeyCode.ENTER,
            ].includes(e.which)) {
                e.preventDefault();
            }
            return panelRef.current.onKeyDown(e);
        }
        /* istanbul ignore next */
        /* eslint-disable no-lone-blocks */
        {
            warning(false, 'Panel not correct handle keyDown event. Please help to fire issue about this.');
            return false;
        }
        /* eslint-enable no-lone-blocks */
    };
    const onInternalBlur = e => {
        if (panelRef.current && panelRef.current.onBlur) {
            panelRef.current.onBlur(e);
        }
    };
    if (operationRef && panelPosition !== 'right') {
        operationRef.current = {
            onKeyDown: onInternalKeyDown,
            onClose: () => {
                if (panelRef.current && panelRef.current.onClose) {
                    panelRef.current.onClose();
                }
            },
        };
    }
    // ============================ Effect ============================
    React.useEffect(() => {
        if (value && !initRef.current) {
            setInnerViewDate(value);
        }
    }, [value]);
    React.useEffect(() => {
        initRef.current = false;
    }, []);
    // ============================ Panels ============================
    let panelNode;
    const pickerProps = Object.assign(Object.assign({}, props), { operationRef: panelRef, prefixCls,
        viewDate, value: mergedValue, onViewDateChange: setViewDate, sourceMode, onPanelChange: onInternalPanelChange, disabledDate });
    delete pickerProps.onChange;
    delete pickerProps.onSelect;
    switch (mergedMode) {
        case 'decade':
            panelNode = (<DecadePanel {...pickerProps} onSelect={(date, type) => {
                    setViewDate(date);
                    triggerSelect(date, type);
                }}/>);
            break;
        case 'year':
            panelNode = (<YearPanel {...pickerProps} onSelect={(date, type) => {
                    setViewDate(date);
                    triggerSelect(date, type);
                }}/>);
            break;
        case 'month':
            panelNode = (<MonthPanel {...pickerProps} onSelect={(date, type) => {
                    setViewDate(date);
                    triggerSelect(date, type);
                }}/>);
            break;
        case 'quarter':
            panelNode = (<QuarterPanel {...pickerProps} onSelect={(date, type) => {
                    setViewDate(date);
                    triggerSelect(date, type);
                }}/>);
            break;
        case 'week':
            panelNode = (<WeekPanel {...pickerProps} onSelect={(date, type) => {
                    setViewDate(date);
                    triggerSelect(date, type);
                }}/>);
            break;
        case 'time':
            delete pickerProps.showTime;
            panelNode = (<TimePanel {...pickerProps} {...(typeof showTime === 'object' ? showTime : null)} onSelect={(date, type) => {
                    setViewDate(date);
                    triggerSelect(date, type);
                }}/>);
            break;
        default:
            if (showTime) {
                panelNode = (<DatetimePanel {...pickerProps} onSelect={(date, type) => {
                        setViewDate(date);
                        triggerSelect(date, type);
                    }}/>);
            }
            else {
                panelNode = (<DatePanel {...pickerProps} onSelect={(date, type) => {
                        setViewDate(date);
                        triggerSelect(date, type);
                    }}/>);
            }
    }
    // ============================ Footer ============================
    let extraFooter;
    let rangesNode;
    const onNow = () => {
        const now = generateConfig.getNow();
        const lowerBoundTime = getLowerBoundTime(generateConfig.getHour(now), generateConfig.getMinute(now), generateConfig.getSecond(now), isHourStepValid ? hourStep : 1, isMinuteStepValid ? minuteStep : 1, isSecondStepValid ? secondStep : 1);
        const adjustedNow = setTime(generateConfig, now, lowerBoundTime[0], // hour
        lowerBoundTime[1], // minute
        lowerBoundTime[2]);
        triggerSelect(adjustedNow, 'submit');
    };
    if (!hideRanges) {
        extraFooter = getExtraFooter(prefixCls, mergedMode, renderExtraFooter);
        rangesNode = getRanges({
            prefixCls,
            components,
            needConfirmButton,
            okDisabled: !mergedValue || (disabledDate && disabledDate(mergedValue)),
            locale,
            showNow,
            onNow: needConfirmButton && onNow,
            onOk: () => {
                if (mergedValue) {
                    triggerSelect(mergedValue, 'submit', true);
                    if (onOk) {
                        onOk(mergedValue);
                    }
                }
            },
        });
    }
    let todayNode;
    if (showToday && mergedMode === 'date' && picker === 'date' && !showTime) {
        const now = generateConfig.getNow();
        const todayCls = `${prefixCls}-today-btn`;
        const disabled = disabledDate && disabledDate(now);
        todayNode = (<a className={classNames(todayCls, disabled && `${todayCls}-disabled`)} aria-disabled={disabled} onClick={() => {
                if (!disabled) {
                    triggerSelect(now, 'mouse', true);
                }
            }}>
                {locale.today}
            </a>);
    }
    return (<PanelContext.Provider value={Object.assign(Object.assign({}, panelContext), { mode: mergedMode, hideHeader: 'hideHeader' in props ? hideHeader : panelContext.hideHeader, hidePrevBtn: inRange && panelPosition === 'right', hideNextBtn: inRange && panelPosition === 'left' })}>
            <div tabIndex={tabIndex} className={classNames(`${prefixCls}-panel`, className, {
            [`${prefixCls}-panel-has-range`]: rangedValue && rangedValue[0] && rangedValue[1],
            [`${prefixCls}-panel-has-range-hover`]: hoverRangedValue && hoverRangedValue[0] && hoverRangedValue[1],
            [`${prefixCls}-panel-rtl`]: direction === 'rtl',
        })} style={style} 
    // onKeyDown={onInternalKeyDown}
    onBlur={onInternalBlur} onMouseDown={onMouseDown} ref={panelDivRef}>
                {panelNode}
                {extraFooter || rangesNode || todayNode ? (<div className={`${prefixCls}-footer`}>
                        {extraFooter}
                        {rangesNode}
                        {todayNode}
                    </div>) : null}
            </div>
        </PanelContext.Provider>);
}
export default PickerPanel;
/* eslint-enable */
